import * as React from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Check, Clock, Phone, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Page } from '@/components/AppShell';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Checkbox,
  EmptyState,
  Field,
  Input,
  LoadingState,
  Select,
  Textarea,
} from '@/components/ui/primitives';
import {
  ConfirmButton,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/overlays';
import { addDaysLocal, formatLocalDate, formatPhone, todayLocalDate } from '@/lib/utils';
import { FOLLOW_UP_TYPES } from '@shared/enums.js';
import type { FollowUp } from '@shared/types.js';

export function FollowUpsPage() {
  const queryClient = useQueryClient();
  const [completing, setCompleting] = React.useState<FollowUp | null>(null);
  const [snoozing, setSnoozing] = React.useState<FollowUp | null>(null);

  const buckets = useQuery({ queryKey: ['follow-ups'], queryFn: api.followUps });

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['follow-ups'] }),
      queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
    ]);
  };

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteFollowUp(id),
    onSuccess: async () => {
      await invalidate();
      toast.success('Follow-up deleted');
    },
    onError: async (error: Error) => {
      await invalidate();
      toast.error(error.message);
    },
  });

  if (buckets.isLoading) return <Page title="Follow-Ups"><LoadingState /></Page>;
  const data = buckets.data!;

  const renderList = (items: FollowUp[], emptyLabel: string) =>
    items.length === 0 ? (
      <EmptyState icon={CalendarClock} title={emptyLabel} />
    ) : (
      <Card>
        <table className="data-table">
          <thead>
            <tr>
              <th className="min-w-[14rem]">Lead</th>
              <th className="w-32">Phone</th>
              <th className="w-28">Type</th>
              <th className="w-32">Due</th>
              <th className="w-24">Priority</th>
              <th>Notes</th>
              <th className="w-56" />
            </tr>
          </thead>
          <tbody>
            {items.map((fu) => {
              const due = fu.snoozedUntilDate ?? fu.dueDate;
              const overdue = due < data.today && fu.status !== 'Completed';
              return (
                <tr key={fu.id}>
                  <td>
                    <Link to={`/leads/${fu.leadId}`} className="font-bold tracking-tight hover:text-primary">
                      {fu.leadBusinessName ?? `Lead #${fu.leadId}`}
                    </Link>
                  </td>
                  <td className="font-mono text-xs tabular-nums">{formatPhone(fu.leadPhone)}</td>
                  <td>{fu.followUpType}</td>
                  <td className={overdue ? 'font-semibold text-amber-700 dark:text-amber-300' : ''}>
                    {formatLocalDate(due)}
                    {fu.hasTime && fu.dueTime && <span className="ml-1 text-muted-foreground">{fu.dueTime}</span>}
                    {!fu.hasTime && <span className="ml-1 text-2xs text-muted-foreground">all day</span>}
                  </td>
                  <td>
                    <Badge variant={fu.priority === 'Urgent' ? 'danger' : fu.priority === 'High' ? 'warning' : 'default'}>
                      {fu.priority}
                    </Badge>
                  </td>
                  <td className="max-w-sm truncate text-muted-foreground">{fu.notes ?? '—'}</td>
                  <td>
                    <div className="flex justify-end gap-1">
                      {fu.followUpType === 'Call' && (
                        <Button asChild variant="outline" size="xs">
                          <Link to={`/queue/lead-${fu.leadId}`}>
                            <Phone className="h-3 w-3" />
                            Call
                          </Link>
                        </Button>
                      )}
                      {fu.status !== 'Completed' && (
                        <>
                          <Button variant="outline" size="xs" onClick={() => setSnoozing(fu)}>
                            <Clock className="h-3 w-3" />
                            Snooze
                          </Button>
                          <Button variant="outline" size="xs" onClick={() => setCompleting(fu)}>
                            <Check className="h-3 w-3" />
                            Complete
                          </Button>
                        </>
                      )}
                      <ConfirmButton
                        title="Delete this follow-up?"
                        confirmLabel="Delete"
                        onConfirm={() => remove.mutateAsync(fu.id)}
                      >
                        <button className="px-1 text-muted-foreground hover:text-red-300">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </ConfirmButton>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    );

  return (
    <Page
      title="Follow-Ups"
      description={`Today is ${formatLocalDate(data.today)}. Dates are calendar dates, so they never shift with daylight saving.`}
    >
      <div className="p-5 md:p-6">
        <Tabs defaultValue={data.counts.overdue > 0 ? 'overdue' : 'today'}>
          <TabsList className="rounded-xl border border-border bg-card p-1 shadow-sm">
            <TabsTrigger value="overdue">
              Overdue
              {data.counts.overdue > 0 && <Badge variant="danger">{data.counts.overdue}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="today">
              Due today
              {data.counts.dueToday > 0 && <Badge variant="primary">{data.counts.dueToday}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="upcoming">
              Upcoming<Badge>{data.counts.upcoming}</Badge>
            </TabsTrigger>
            <TabsTrigger value="snoozed">
              Snoozed<Badge>{data.counts.snoozed}</Badge>
            </TabsTrigger>
            <TabsTrigger value="completed">Completed</TabsTrigger>
          </TabsList>

          <TabsContent value="overdue" className="pt-3">
            {renderList(data.overdue, 'Nothing overdue')}
          </TabsContent>
          <TabsContent value="today" className="pt-3">
            {renderList(data.dueToday, 'Nothing due today')}
          </TabsContent>
          <TabsContent value="upcoming" className="pt-3">
            {renderList(data.upcoming, 'Nothing scheduled in the next 30 days')}
          </TabsContent>
          <TabsContent value="snoozed" className="pt-3">
            {renderList(data.snoozed, 'Nothing snoozed')}
          </TabsContent>
          <TabsContent value="completed" className="pt-3">
            {renderList(data.completed, 'Nothing completed yet')}
          </TabsContent>
        </Tabs>
      </div>

      <CompleteDialog followUp={completing} onClose={() => setCompleting(null)} onDone={invalidate} />
      <SnoozeDialog followUp={snoozing} onClose={() => setSnoozing(null)} onDone={invalidate} />
    </Page>
  );
}

function CompleteDialog({
  followUp,
  onClose,
  onDone,
}: {
  followUp: FollowUp | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [notes, setNotes] = React.useState('');
  const [createAnother, setCreateAnother] = React.useState(false);
  const [type, setType] = React.useState('Call');
  const [date, setDate] = React.useState(addDaysLocal(todayLocalDate(), 7));

  React.useEffect(() => {
    if (followUp) {
      setNotes('');
      setCreateAnother(false);
      setType(followUp.followUpType);
      setDate(addDaysLocal(todayLocalDate(), 7));
    }
  }, [followUp]);

  const complete = useMutation({
    mutationFn: () =>
      api.completeFollowUp(followUp!.id, {
        notes: notes.trim() || null,
        createAnother: createAnother ? { followUpType: type, dueDate: date } : null,
      }),
    onSuccess: () => {
      onDone();
      onClose();
      toast.success(createAnother ? 'Completed and next one scheduled' : 'Follow-up completed');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={!!followUp} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Complete follow-up</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <Field label="What happened" hint="Saved as a note on the lead.">
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} autoFocus />
          </Field>
          <label className="flex items-center gap-2 text-xs">
            <Checkbox checked={createAnother} onCheckedChange={(v) => setCreateAnother(!!v)} />
            Schedule another follow-up
          </label>
          {createAnother && (
            <div className="grid grid-cols-2 gap-2">
              <Field label="Type">
                <Select value={type} onChange={(e) => setType(e.target.value)}>
                  {FOLLOW_UP_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Due date">
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </Field>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" loading={complete.isPending} onClick={() => complete.mutate()}>
            Complete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SnoozeDialog({
  followUp,
  onClose,
  onDone,
}: {
  followUp: FollowUp | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [date, setDate] = React.useState(addDaysLocal(todayLocalDate(), 3));

  React.useEffect(() => {
    if (followUp) setDate(addDaysLocal(todayLocalDate(), 3));
  }, [followUp]);

  const snooze = useMutation({
    mutationFn: () => api.snoozeFollowUp(followUp!.id, date),
    onSuccess: () => {
      onDone();
      onClose();
      toast.success('Snoozed');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={!!followUp} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Snooze follow-up</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <Field label="Snooze until">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
          <div className="flex flex-wrap gap-1.5">
            {[1, 3, 7, 14, 30].map((days) => (
              <Button
                key={days}
                variant="outline"
                size="xs"
                onClick={() => setDate(addDaysLocal(todayLocalDate(), days))}
              >
                +{days}d
              </Button>
            ))}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" loading={snooze.isPending} onClick={() => snooze.mutate()}>
            Snooze
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
